import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { searchAppointments, type DayRdv } from '../../lib/refonteApi';
import { LIFECYCLE, type LifecycleStatus } from '../../lib/appointmentLifecycle';
import RdvTabs from '../../components/refonte/RdvTabs';
import RdvDetail from '../../components/refonte/RdvDetail';
import StatusBadge from '../../components/refonte/StatusBadge';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const norm = (s: string) => s.toLowerCase().trim();

// Recherche RDV globale : taper le nom d'un prospect/société pour retrouver un RDV vite (sans
// passer par le client), voir le détail et le modifier. SDR = ses RDV par défaut, manager = tous.
const RdvSearchPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'sdr';
  const meId = profile?.user_id ?? null;
  const isProspecter = role === 'sdr' || !!profile?.prospecting_team;

  const [rows, setRows] = useState<DayRdv[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'mine' | 'all'>(isProspecter ? 'mine' : 'all');
  const [sdrFilter, setSdrFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selId, setSelId] = useState<string | null>(null);

  // Fenêtre : 60 jours passés → 30 jours à venir (couvre « le RDV que je viens de rentrer »).
  const range = useMemo(() => {
    const now = new Date();
    const f = new Date(now); f.setDate(f.getDate() - 60);
    const t = new Date(now); t.setDate(t.getDate() + 30);
    return { from: iso(f), to: iso(t) };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await searchAppointments(range.from, range.to)); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const sdrNames = useMemo(() => [...new Set(rows.map(r => r.sdr?.sales_name).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b)), [rows]);
  const statuses = useMemo(() => [...new Set(rows.map(r => r.lifecycle_status).filter(Boolean) as LifecycleStatus[])], [rows]);

  const filtered = useMemo(() => {
    const t = norm(q);
    return rows.filter(r => {
      if (scope === 'mine' && meId && (r as any).sdr_id !== meId) return false;
      if (sdrFilter && (r.sdr?.sales_name || '') !== sdrFilter) return false;
      if (statusFilter && (r.lifecycle_status || '') !== statusFilter) return false;
      if (t && !`${r.prospect_name || ''} ${r.company_name || ''} ${r.clients?.name || ''} ${r.phone || ''}`.toLowerCase().includes(t)) return false;
      return true;
    });
  }, [rows, q, scope, meId, sdrFilter, statusFilter]);
  const sel = rows.find(r => r.id === selId) || null;

  useEffect(() => { if (filtered.length && !filtered.some(r => r.id === selId)) setSelId(filtered[0].id); }, [filtered, selId]);

  const inputCls = 'rounded-lg border border-line-2 bg-panel px-3 h-8 text-[12.5px]';

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Rechercher un RDV</h1>
      <RdvTabs />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {([['mine', 'Mes RDV'], ['all', 'Tous']] as const).map(([k, lbl]) =>
              <button key={k} onClick={() => setScope(k)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md ${scope === k ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{lbl}</button>)}
          </div>
          {scope === 'all' && sdrNames.length > 1 && (
            <select value={sdrFilter} onChange={e => setSdrFilter(e.target.value)} className={inputCls} title="Filtrer par SDR">
              <option value="">Tous les SDR</option>
              {sdrNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputCls} title="Filtrer par statut">
            <option value="">Tous statuts</option>
            {statuses.map(s => <option key={s} value={s}>{LIFECYCLE[s]?.label ?? s}</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Prospect, société, tél…" className={inputCls + ' w-56'} />
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">Tape un nom pour retrouver un RDV (60 derniers jours → 30 à venir), puis ouvre-le pour le modifier. {filtered.length} résultat{filtered.length > 1 ? 's' : ''}.</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 lg:h-[78vh] items-stretch">
        <div className="bg-panel border border-line rounded-card p-3 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? <SkeletonRows count={8} /> : filtered.length === 0 ? (
              <EmptyState title="Aucun RDV trouvé" hint="Ajuste la recherche, le statut, ou passe en « Tous »." />
            ) : (
              <div className="divide-y divide-line">
                {filtered.map(rdv => (
                  <button key={rdv.id} onClick={() => setSelId(rdv.id)}
                    className={`w-full text-left py-2.5 flex items-center gap-3 flex-wrap hover:bg-panel-2 transition rounded-lg px-2 -mx-1 ${selId === rdv.id ? 'bg-panel-2 ring-1 ring-line-2' : ''}`}>
                    <span className="font-mono text-[10px] text-faint w-24 shrink-0">{rdv.date} {rdv.time?.slice(0, 5) || ''}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate">{rdv.prospect_name || 'Prospect'}{rdv.company_name && <span className="text-dim font-normal"> · {rdv.company_name}</span>}</div>
                      <div className="font-mono text-[10px] text-faint">{rdv.clients?.name || '—'} · SDR {rdv.sdr?.sales_name || '—'}</div>
                    </div>
                    <StatusBadge status={rdv.lifecycle_status ?? null} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="bg-panel border border-line rounded-card p-5 overflow-y-auto">
          {sel ? <RdvDetail rdv={sel} role={role} onChanged={load} />
            : <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un RDV" hint="Clique un RDV dans la liste pour voir sa fiche et le modifier." /></div>}
        </div>
      </div>
    </div>
  );
};

export default RdvSearchPage;
